import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormNumberInput from '../../../components/form/FormNumberInput';
import FormTextarea from '../../../components/form/FormTextarea';

import { vbAddCredit } from '../../../store/slices/voiceBroadcastBillingSlice';
import { companyListGet } from '../../../store/slices/adminSlices/companySlice';

const PAYMENT_METHODS = [
  { label: 'Bank Transfer', value: 'Bank Transfer' },
  { label: 'NEFT', value: 'NEFT' },
  { label: 'RTGS', value: 'RTGS' },
  { label: 'UPI', value: 'UPI' },
  { label: 'Cheque', value: 'Cheque' },
  { label: 'Cash', value: 'Cash' },
  { label: 'Other', value: 'Other' }
];

// Record an offline payment as prepaid credit. 1 credit = Rs 1.
//
// `company` may be a row from the accounts table (company pre-selected) or {}
// when opened from the header, in which case the company must be chosen.
const AddCreditModal = ({ isOpen, company, onCloseClick }) => {
  const dispatch = useDispatch();
  const [companyOptions, setCompanyOptions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const preselected = company?.company_id;

  const initialValues = {
    company_id: '',
    amount: '',
    payment_method: 'Bank Transfer',
    reference_id: '',
    notes: ''
  };

  const validationSchema = Yup.object({
    company_id: Yup.number().required('Company is required'),
    amount: Yup.number()
      .typeError('Amount must be a number')
      .moreThan(0, 'Amount must be greater than 0')
      .required('Amount is required'),
    payment_method: Yup.string().required('Payment method is required')
  });

  const onSubmit = async () => {
    setIsSaving(true);
    try {
      const response = await dispatch(
        vbAddCredit({
          company_id: Number(values.company_id),
          amount: Number(values.amount),
          payment_method: values.payment_method,
          reference_id: values.reference_id || undefined,
          notes: values.notes || undefined
        })
      ).unwrap();

      if (response?.success) {
        toast.success(response?.message || 'Credit added successfully');
        onCloseClick(true);
        resetForm();
      }
    } catch (error) {
      // axiosApi already toasts the server message.
      console.log('error', error);
    } finally {
      setIsSaving(false);
    }
  };

  const { values, handleChange, handleBlur, handleSubmit, touched, errors, resetForm, setValues } =
    useFormik({ initialValues, validationSchema, onSubmit });

  const getCompanyOptions = async () => {
    try {
      const response = await dispatch(companyListGet({ limit: 1000, page: 1 })).unwrap();
      setCompanyOptions(
        (response?.data || []).map((val) => ({ label: val?.name, value: val?.id }))
      );
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getCompanyOptions();
      setValues({ ...initialValues, company_id: preselected || '' });
    }
  }, [isOpen]);

  const amountNum = Number(values.amount) || 0;

  return (
    <Modal isOpen={isOpen} size="lg" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">Add VoiceBroadcast Credit</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="vb-add-credit" onSubmit={handleSubmit}>
          <Row>
            <Col lg="6">
              <FormSelect
                name="company_id"
                label="Company"
                placeholder="Select Company"
                value={values.company_id}
                options={companyOptions}
                disabled={!!preselected}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                menuPlacement="bottom"
              />
            </Col>
            <Col lg="6">
              <FormNumberInput
                name="amount"
                label="Amount Received (Rs)"
                placeholder="e.g. 10000"
                min={0}
                step="0.01"
                value={values.amount}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="6">
              <FormSelect
                name="payment_method"
                label="Payment Method"
                placeholder="Select Payment Method"
                value={values.payment_method}
                options={PAYMENT_METHODS}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                menuPlacement="bottom"
              />
            </Col>
            <Col lg="6">
              <FormInput
                name="reference_id"
                label="Reference No. (optional)"
                placeholder="UTR / Cheque no. / UPI ref"
                value={values.reference_id}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="12">
              <FormTextarea
                name="notes"
                label="Notes (optional)"
                placeholder="e.g. Monthly prepaid received on 28-Oct"
                value={values.notes}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>

          <div className="mt-2 p-2 rounded" style={{ backgroundColor: 'rgba(44,171,227,0.1)' }}>
            <div style={{ fontSize: '13px' }}>
              1 credit = Rs 1. This records an offline payment and increases the company&apos;s
              credit immediately.
              {amountNum > 0 && (
                <>
                  {' '}
                  Rs {amountNum.toLocaleString('en-IN')} adds{' '}
                  <strong>{amountNum.toLocaleString('en-IN')} credits</strong> — about{' '}
                  {Math.floor(amountNum / 0.18).toLocaleString('en-IN')} calls of 16-30s at the
                  default normal rate.
                </>
              )}
            </div>
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button
          form="vb-add-credit"
          type="submit"
          disabled={isSaving}
          className="px-5 bg-primary border-primary">
          {isSaving ? 'Adding...' : 'Add Credit'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddCreditModal.propTypes = {
  isOpen: PropTypes.bool,
  company: PropTypes.any,
  onCloseClick: PropTypes.func
};

export default AddCreditModal;
